/**
 * A company's environment vocabulary: the type, the derivation from the two
 * booleans, and the visible labels.
 *
 * **It lives apart from `CompanySelect`** because a component file that also
 * exports functions and constants breaks Vite's Fast Refresh
 * (`react-refresh/only-export-components`), the same resolution as
 * `company-options.ts` and `lib/aria.ts`.
 *
 * And there is a deeper reason, beyond the lint: **this does not belong to the
 * selector**. A company's environment is the second line of defence after
 * `AppShell`'s badge, and both exist so that nobody issues believing they are
 * testing. It is consumed by the badge, the layout, the company listing and the
 * issuing screen; none of them needs to mount a `<select>` in order to ask
 * which environment a company is in.
 */

/**
 * The three environments come from TWO booleans on `company`. The database's
 * `company_sandbox_not_production_check` CHECK forbids sandbox + production at
 * once, so the derivation is total: sandbox -> 'sandbox'; production ->
 * 'production'; neither -> 'certification'.
 */
export type CompanyEnvironment = 'production' | 'certification' | 'sandbox';

export interface CompanyOption {
  id: string;
  legalName: string;
  taxNumber: string;
  isProduction: boolean;
  isSandbox: boolean;
}

/**
 * Derives a company's environment from its two booleans.
 *
 * Written so that a fourth case IS NOTICED: the database's CHECK forbids
 * `isSandbox && isProduction`, but this function does not silently assume which
 * of the two "wins" should that guarantee ever break upstream (the CHECK
 * itself, a test mock, a badly built API response) — it throws instead of
 * guessing, because guessing in a company selector makes people issue under the
 * wrong company with nobody the wiser.
 */
export function deriveCompanyEnvironment(
  company: Pick<CompanyOption, 'isProduction' | 'isSandbox'>,
): CompanyEnvironment {
  if (company.isSandbox && company.isProduction) {
    throw new Error(
      'CompanyOption with isSandbox and isProduction both true: ' +
        'inconsistent with the company_sandbox_not_production_check CHECK.',
    );
  }
  if (company.isSandbox) return 'sandbox';
  if (company.isProduction) return 'production';
  return 'certification';
}

/** Each environment's visible name. Unabbreviated: they are the second line of
 *  defence, so "Prod"/"Cert" is not enough at the moment of choosing.
 *
 * Exported (not only consumed here) because `EnvironmentBadge`
 * (`features/companies/environment-badge.tsx`) needs the exact same label: two
 * copies of the same string is exactly how a badge and a selector end up
 * saying different things about the same company (contract §5.7 of the
 * inventory). */
export const COMPANY_ENVIRONMENT_LABELS: Readonly<Record<CompanyEnvironment, string>> = {
  production: 'Producción',
  certification: 'Certificación',
  sandbox: 'Sandbox',
};
